import React, { useEffect, useState } from "react";
import { shallow } from "zustand/shallow";
import { useSearchParams } from "react-router-dom";
import {
  Title,
  Flex,
  Box,
  Text,
  Button,
  Grid,
  UnstyledButton,
  Modal,
  Stack,
} from "@mantine/core";
import axios from "axios";
import {
  IconWheel,
  IconUser,
  IconCalendar,
  IconExclamationCircle,
  IconAward,
  IconMedal,
  IconLocationFilled,
  IconSpeakerphone,
} from "@tabler/icons-react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import ViewTitle from "@components/Microsite/ViewTitle";
import Wrapper from "./RideChallengeWrapper";
import useAppStore from "@components/Microsite/microsite-store";

import RevScore from "./RevScore";
import UserMilestones from "./UserMilestones";
import DashboardContests from "./DashboardContests";
import DashboardVehicles from "./DashboardVehicles";
import DashboardMembership from "./DashboardMembership";
import DashboardUnverified from "./DashboardUnverified";
import DashboardHistory from "./DashboardHistory";
import RideChallengeKeywordForm from "./RideChallengeKeywordForm";
import RideChallengeAlerts from "./RideChallengeAlerts";
import EffortPickerDeux from "@components/Microsite/EffortPickerDeux";
import { IframeModal } from "@components/shared";
import { StyledImage, StyledPlaceholder } from "./UserHistory";
import logo from "@assets/images/lets-ride-logo.png";
import vehicleDataEffortLogo from "@assets/images/vehicle-data-effort-logo.png";
import VehicleDataOdometer from "./VehicleDataOdometer";
import MembershipRequiredModal from "./MembershipRequiredModal";
import RideChallengeCheckInButton from "./RideChallengeCheckInButton";
import PreferredLocation from "@components/Microsite/PreferredLocation";
import { getPlacementImage } from "@components/Asset/helpers";
import { placementDefs } from "@components/Asset/placementDefs";

export const views = {
  dashboard: "dashboard",
  membership: "membership",
  vehicles: "vehicles",
  unverified_data: "unverified_data",
  keyword: "keyword",
  rev_score: "rev_score",
  milestones: "milestones",
  contest: "contest",
  history: "history",
  preferred_location: "preferred_location",
};

export default function RideChallengeDashboard() {
  const {
    alerts,
    appConfig,
    appLoading,
    coords,
    dashboardData,
    email,
    location,
    phone,
    setAppLoading,
    setDashboardData,
    user,
    entityInfo,
    preferredLocation,
  } = useAppStore(
    (state) => ({
      alerts: state.alerts,
      appConfig: state.appConfig,
      appLoading: state.appLoading,
      coords: state.coords,
      dashboardData: state.dashboardData,
      email: state.email,
      location: state.location,
      phone: state.phone,
      setAppLoading: state.setAppLoading,
      setDashboardData: state.setDashboardData,
      user: state.user,
      entityInfo: state.entityInfo,
      preferredLocation: state.preferredLocation,
    }),
    shallow
  );
  const [view, setView] = useState(views.dashboard);
  const [error, setError] = useState(null);
  const [showOtherCampaigns, setShowOtherCampaigns] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();

  useEffect(() => {
    setShowOtherCampaigns(false);
  }, [entityInfo.campaign_effort_id]);

  useEffect(() => {
    if (view !== views.dashboard) return;
    fetchData();
  }, [view, entityInfo.campaign_effort_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const isVehicleDataEffort = entityInfo.is_vehicle_data_effort;

  const allowMileageEntry = isVehicleDataEffort
    ? true
    : dashboardData
      ? !dashboardData.max_mileage_attained
      : true;

  function fetchData() {
    if (
      (!user.hog_membership_data ||
        (user.hog_membership_data.confirmed_by_third_party_api === false &&
          user.hog_membership_data.phone_match === false)) && !isVehicleDataEffort
    ) {
      return setView(views.membership);
    }

    const req = {
      user_id: user?.id,
    };

    const locationId = location?.id;

    setError(null);

    if (locationId) req.location_id = locationId;
    if (entityInfo.campaign_effort_id)
      req.campaign_effort_id = entityInfo.campaign_effort_id;

    if (email) req.email = email;
    if (phone) req.mobile_phone = phone;

    if (coords) {
      req.latitude = coords.latitude;
      req.longitude = coords.longitude;
    }

    setAppLoading(true);

    axios
      .post(`/ride-challenge/app-dashboard/`, req)
      .then(({ data }) => {
        const res = data.response[0];

        if (
          (!res.hog_membership_data ||
            (res.hog_membership_data.confirmed_by_third_party_api === false &&
              res.hog_membership_data.phone_match === false)) && !isVehicleDataEffort
        ) {
          setDashboardData(null);
          setAppLoading(false);
          setView(views.membership);
          return;
        }

        setDashboardData(res);
        setAppLoading(false);
      })
      .catch((err) => {
        setError(err);
        setAppLoading(false);
        // setUser(null);
      });
  }

  if (error) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <div style={{ minHeight: "100px", marginTop: "25px" }}>
            <Text align="center" fw={600} c="red">
              {error}
            </Text>
            <Flex justify="center" mt="lg">
              <Button onClick={fetchData} size="xs" variant="light">
                Reload
              </Button>
            </Flex>
          </div>
        </div>
      </Wrapper>
    );
  }

  if (view === views.milestones) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <AnimatedWrapper>
            <ViewTitle title={`My Milestones`} />
            <div style={{ minHeight: "250px" }}>
              <UserMilestones
                loading={appLoading}
                setAppLoading={(val) => setAppLoading(val)}
                reqData={{
                  campaign_effort_id: entityInfo.campaign_effort_id,
                  user_id: user.id,
                }}
              />
            </div>
            <Box mt="xl">
              <DashboardButton onClick={() => setView(views.dashboard)} />
            </Box>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (view === views.history) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <AnimatedWrapper>
            <ViewTitle title={`My History`} />
            <div style={{ minHeight: "250px" }}>
              <DashboardHistory />
            </div>
            <Box mt="xl">
              <DashboardButton onClick={() => setView(views.dashboard)} />
            </Box>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (view === views.rev_score) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <RevScore
            color={appConfig.colors.base}
            reqData={{
              user_id: user.id,
              campaign_effort_id: entityInfo.campaign_effort_id,
            }}
            expanded
            tiers={dashboardData?.contest_tiers?.results[0]?.tier_results}
            gaugeMaxValue={
              dashboardData?.contest_tiers?.results[0]?.gauge_max_value
            }
          />
          <Box mt="xl">
            <DashboardButton onClick={() => setView(views.dashboard)} />
          </Box>
        </div>
      </Wrapper>
    );
  }

  if (view === views.contest) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <AnimatedWrapper>
            {/* <Flex justify="center" mb="sm">
            <IconAward
              size={48}
              style={{
                stroke: appConfig.colors.icon,
              }}
            />
          </Flex> */}
            <ViewTitle title="My Progress" />
            <DashboardContests />
            <Box mt="xl">
              <DashboardButton onClick={() => setView(views.dashboard)} />
            </Box>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (view === views.vehicles) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <AnimatedWrapper>
            {/* <Flex justify="center" mb="sm">
            <IconWheel
              size={48}
              style={{
                stroke: appConfig.colors.icon,
              }}
            />
          </Flex> */}
            <DashboardVehicles
              setDashboardView={() => setView(views.dashboard)}
              setUnverifiedView={() => setView(views.unverified_data)}
              fetchData={fetchData}
              allowMileageEntry={allowMileageEntry}
            >
              <DashboardButton onClick={() => setView(views.dashboard)} />
            </DashboardVehicles>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (view === views.membership) {
    return (
      <Wrapper>
        <div>
          {/* <Header /> */}
          <AnimatedWrapper>
            {/* <Flex justify="center" mb="sm">
            <IconUser
              size={48}
              style={{
                stroke: appConfig.colors.icon,
              }}
            />
          </Flex> */}
            {/* <ViewTitle title={`Membership`} /> */}
            <DashboardMembership
              onMembershipCreated={() => setView(views.dashboard)}
            >
              <DashboardButton onClick={() => setView(views.dashboard)} />
            </DashboardMembership>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (view === views.unverified_data) {
    return (
      <Wrapper>
        <div>
          <Header activeView={view} />
          <AnimatedWrapper>
            {/* <Flex justify="center" mb="sm">
            <IconExclamationCircle
              size={48}
              style={{
                stroke: "var(--mantine-color-red-7)",
              }}
            />
          </Flex> */}
            <ViewTitle title={`These items need attention`} />
            <div style={{ minHeight: "150px" }}>
              <DashboardUnverified
                setView={() => setView(views.dashboard)}
                allowMileageEntry={allowMileageEntry}
              >
                <DashboardButton onClick={() => setView(views.dashboard)} />
              </DashboardUnverified>
            </div>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (view === views.preferred_location) {
    return (
      <Wrapper>
        <div>
          <Header />
          <AnimatedWrapper>
            {/* <Flex justify="center" mb="sm">
            <IconUser
              size={48}
              style={{
                stroke: appConfig.colors.icon,
              }}
            />
          </Flex> */}
            {/* <ViewTitle title={`Membership`} /> */}
            <PreferredLocation
              onSubmit={() => {
                setView(views.dashboard);
              }}
            >
              <DashboardButton onClick={() => setView(views.dashboard)} />
            </PreferredLocation>
          </AnimatedWrapper>
        </div>
      </Wrapper>
    );
  }

  if (!dashboardData)
    return (
      <Wrapper>
        <div style={{ minHeight: "300px" }}>
          <Header activeView={view} />
        </div>
      </Wrapper>
    );

  const navButtonConfig = {
    size: 28,
    color: "#000",
  };

  const navButtons = [
    {
      text: t("dashboard_log_miles_button"),
      icon: (
        <IconWheel
          size={navButtonConfig.size}
          style={{ stroke: navButtonConfig.color }}
        />
      ),
      onClick: () => setView(views.vehicles),
    },
    {
      text: isVehicleDataEffort ? "View Ride 365 Stats" : "My History",
      link: isVehicleDataEffort
        ? "https://members.harley-davidson.com/HOG/HDMN_Ride365?PublicAccess=true&PublicAccess=true&site=a0dj0000006mPzAAAU&source_cd=undefined"
        : null,
      icon: (
        <IconCalendar
          size={navButtonConfig.size}
          style={{ stroke: navButtonConfig.color }}
        />
      ),
      onClick: () => {
        setView(views.history);
        setAppLoading(true);
      },
    },
    {
      text: t("dashboard_milestones_button"),
      icon: (
        <IconMedal
          size={navButtonConfig.size}
          style={{ stroke: navButtonConfig.color }}
        />
      ),
      onClick: () => setView(views.milestones),
      has_alert: alerts.some((alert) => alert.milestone_id),
      vehicle_data_effort: false,
    },
    {
      text: t("dashboard_tier_progress_button"),
      icon: (
        <IconAward
          size={navButtonConfig.size}
          style={{ stroke: navButtonConfig.color }}
        />
      ),
      onClick: () => setView(views.contest),
      vehicle_data_effort: false,
    },
    {
      text: "Membership",
      icon: (
        <IconUser
          size={navButtonConfig.size}
          style={{ stroke: navButtonConfig.color }}
        />
      ),
      onClick: () => setView(views.membership),
      span: entityInfo.location ? 6 : 12,
      centered: true,
    },
    {
      text: "More Campaigns",
      icon: (
        <IconSpeakerphone
          size={navButtonConfig.size}
          style={{ stroke: navButtonConfig.color }}
        />
      ),
      onClick: () => setShowOtherCampaigns(true),
      span: 6,
      centered: true,
      show: entityInfo.location ? true : false,
    },
  ]
    .filter((f) =>
      isVehicleDataEffort ? f.vehicle_data_effort !== false : true
    )
    .filter((f) => f.show !== false);

  const statBlocks = [
    {
      text: t("odometer_readings"),
      value: dashboardData.readings_engagements_count,
    },
    {
      text: t("keywords_claimed"),
      value: dashboardData.keyword_engagements_count,
      vehicle_data_effort: false,
    },
    {
      text: t("milestones_earned"),
      value: dashboardData.milestones_achieved,
      vehicle_data_effort: false,
    },
    { text: t("dealerships_visited"), value: dashboardData.locations_visited },
  ].filter((f) =>
    isVehicleDataEffort ? f.vehicle_data_effort !== false : true
  );

  if (showOtherCampaigns) {
    return (
      <Wrapper>
        <div>
          <AnimatedWrapper>
            <EffortPickerDeux
              reqData={{
                campaign_effort_id: entityInfo.campaign_effort_id,
                user_id: user.id,
                location_id: location?.id,
              }}
              location={location}
              beforeFetch={() => {
                setShowOtherCampaigns(false);
                setDashboardData(null);
              }}
            />
          </AnimatedWrapper>
        </div>
        <Flex justify="center" mt="lg">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => setShowOtherCampaigns(false)}
          >
            Back to dashboard
          </Button>
        </Flex>
      </Wrapper>
    );
  }

  return (
    <Box radius="md">
      {entityInfo.require_membership && !dashboardData.hog_membership_data && (
        <MembershipRequiredModal setView={setView} />
      )}
      {!preferredLocation && (
        <PreferredLocationModal
          setView={() => setView(views.preferred_location)}
        />
      )}
      <Box
        style={{
          backgroundColor: "#000",
          borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
        }}
        pt={
          dashboardData.has_unverified_readings && allowMileageEntry
            ? "0"
            : "lg"
        }
      >
        {dashboardData.has_unverified_readings && allowMileageEntry && (
          <UnstyledButton
            style={{ width: "100%" }}
            onClick={() => setView(views.unverified_data)}
          >
            <StyledUnverifiedReadings>
              <Flex
                align="center"
                gap="xs"
                justify="center"
                style={{ width: "100%" }}
              >
                <IconExclamationCircle size={20} color="white" />
                <Text align="center" size="md" fw={600} c="white">
                  YOU HAVE UNVERIFED READINGS TAP HERE
                </Text>
              </Flex>
            </StyledUnverifiedReadings>
          </UnstyledButton>
        )}
        <Header activeView={view} margin={"0px"} />
        {isVehicleDataEffort ? (
          <>
            <Box>
              {entityInfo.campaign_effort_id && <VehicleDataOdometer />}
            </Box>
          </>
        ) : (
          <Box>
            {entityInfo.campaign_effort_id && (
              <RevScore
                reqData={{
                  user_id: user.id,
                  campaign_effort_id: entityInfo.campaign_effort_id,
                }}
                onRevScoreClick={() => setView(views.rev_score)}
                // tiers={
                //   dashboardData?.contest_tiers?.results[0]?.tier_results || []
                // }
                tiers={
                  dashboardData.contest_tiers &&
                    dashboardData.contest_tiers.results &&
                    dashboardData.contest_tiers.results.length
                    ? dashboardData.contest_tiers.results[0].tier_results
                    : []
                }
                revScore={dashboardData.rev_score}
                color={appConfig.colors.base}
                // gaugeMaxValue={
                //   dashboardData?.contest_tiers?.results[0]?.gauge_max_value
                // }
                gaugeMaxValue={
                  dashboardData.contest_tiers &&
                    dashboardData.contest_tiers.results &&
                    dashboardData.contest_tiers.results.length
                    ? dashboardData.contest_tiers.results[0].gauge_max_value
                    : []
                }
              />
            )}
          </Box>
        )}
      </Box>
      <StyledDashboardWrapper isVehicleDataEffort={isVehicleDataEffort}>
        {!isVehicleDataEffort && (
          <>
            {!dashboardData.needs_initial_reading ? (
              <Box mb="lg" mt="lg">
                {entityInfo.use_keywords && (
                  <RideChallengeKeywordForm
                    effortId={entityInfo.campaign_effort_id}
                    onSuccess={() => {
                      fetchData();
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box mt="lg" mb="lg"></Box>
            )}
          </>
        )}
        <Grid mb="lg">
          {entityInfo.use_checkins && (
            <RideChallengeCheckInButton onSuccess={() => fetchData()} />
          )}
          {navButtons.map((navButton, i) => (
            <Grid.Col span={navButton.span ? navButton.span : 6} key={i}>
              <UnstyledButton
                style={{ width: "100%" }}
                onClick={() => {
                  if (navButton.link) {
                    window.open(navButton.link, "_blank");
                  } else {
                    navButton.onClick();
                  }
                }}
              >
                <NavButton
                  color={navButton.has_alert ? appConfig.colors.base : ""}
                  hasAlert={navButton.has_alert}
                >
                  <Flex
                    align="center"
                    gap="xs"
                    // justify={navButton.centered ? "center" : "flex-start"}
                    justify="center"
                  >
                    {navButton.icon}
                    <Text size="sm" fw={600}>
                      {navButton.text}
                    </Text>
                  </Flex>
                </NavButton>
              </UnstyledButton>
            </Grid.Col>
          ))}
        </Grid>
        {/* <Box mt="lg" mb="lg">
        <MembershipButton
          color={appConfig.colors.base}
          onClick={() => setView(views.membership)}
          membership={dashboardData.hog_membership_data}
        />
      </Box> */}
        {!isVehicleDataEffort && (
          <Grid mb="lg">
            {statBlocks.map((statBlock, i) => (
              <Grid.Col span={6} key={i}>
                <StatBlock color={appConfig.colors.base}>
                  <Text size="xs" align="center" className="label">
                    {statBlock.text}
                  </Text>
                  <Title order={2} c="dark" align="center" className="value">
                    {statBlock.value}
                  </Title>
                </StatBlock>
              </Grid.Col>
            ))}
          </Grid>
        )}
        {location && (
          <Box my="xl">
            <LocationInfo
              info={location}
              color={appConfig.colors.base}
              title={t("location_visit_text")}
              showGeo={location.present_at_location}
            />
          </Box>
        )}
        {preferredLocation ? (
          <Box my="xl">
            <LocationInfo
              info={preferredLocation}
              color={appConfig.colors.base}
              title={"Home Dealership"}
              showGeo={false}
              onClick={() => {
                setView(views.preferred_location);
              }}
            />
          </Box>
        ) : (
          <Box my="xl">
            <LocationInfo
              info={{ name: "Set your home dealership" }}
              color={appConfig.colors.base}
              title={"Home Dealership"}
              showGeo={false}
              onClick={() => setView(views.preferred_location)}
            />
          </Box>
        )}
        {user.hog_chapter_data && (
          <Box my="xl">
            <LocationInfo
              info={user.hog_chapter_data}
              color={appConfig.colors.base}
              title={"My HOG Chapter"}
            />
          </Box>
        )}
        <Flex justify="center" gap="xs">
          <IframeModal
            src="https://www.mightyloud.com/rc-faq"
            buttonText={isVehicleDataEffort ? "FAQ" : "FAQ & Rules"}
            buttonProps={{
              variant: "subtle",
              color: "gray",
              size: "xs",
            }}
          />
          {/* {entityInfo.location && (
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              onClick={() => setShowOtherCampaigns(true)}
            >
              View other campaigns
            </Button>
          )} */}
        </Flex>
      </StyledDashboardWrapper>
    </Box>
  );
}

const AnimatedWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0 }}
    // style={box}
    key="box"
  >
    {children}
  </motion.div>
);

const DashboardButton = ({ onClick }) => {
  return (
    <Flex justify="center">
      <Button
        onClick={onClick}
        // variant="light"
        c="dark"
        color="gray.1"
        size="xs"
        radius="lg"
      >
        back to dashboard
      </Button>
    </Flex>
  );
};

const StyledLogo = styled.img`
  /* width: 60%; */
  display: block;
  margin: 0 auto;
  max-height: 150px;
  max-width: 300px;
`;

export const NavButton = styled.div`
  /* background: ${(props) =>
    `var(--mantine-color-${props.color}-light-hover)`}; */
  /* border: ${(props) => `4px solid var(--mantine-color-${props.color}-7)`}; */
  border: ${(props) =>
    props.hasAlert
      ? `4px solid var(--mantine-color-${props.color}-7)`
      : `4px solid var(--mantine-color-dark-7)`};
  width: 100%;
  border-radius: 10px;
  /* color: #fff; */
  color: #000;
  padding: 10px;
  text-transform: uppercase;

  p {
    color: ${(props) =>
    props.hasAlert
      ? `var(--mantine-color-${props.color}-7)`
      : `var(--mantine-color-dark-7)`};
  }

  svg {
    stroke: ${(props) =>
    props.hasAlert
      ? `var(--mantine-color-${props.color}-7) !important`
      : `var(--mantine-color-dark-7)`};
  }
`;

const StatBlock = styled.div`
  background: var(--mantine-color-gray-2);
  border-radius: 10px;
  padding: 10px;

  .value {
    font-weight: 700;
  }

  .label {
    color: ${(props) => `var(--mantine-color-${props.color}-7)`};
    text-transform: uppercase;
    font-weight: 600;
  }
`;

const Header = ({ activeView, margin }) => {
  const { entityInfo, appConfig } = useAppStore(
    (state) => ({
      entityInfo: state.entityInfo,
      appConfig: state.appConfig,
    }),
    shallow
  );
  const isVehicleDataEffort = entityInfo.is_vehicle_data_effort;
  const headerLogo = getPlacementImage(
    appConfig,
    entityInfo.assets,
    placementDefs.header_logo
  );
  const logoPath = headerLogo
    ? headerLogo
    : isVehicleDataEffort
      ? vehicleDataEffortLogo
      : logo;

  return (
    <>
      <Flex
        justify="center"
        mb={isVehicleDataEffort ? margin || "lg" : "0"}
        pt={isVehicleDataEffort ? "sm" : "0"}
      >
        <StyledLogo src={logoPath} />
      </Flex>
      <RideChallengeAlerts activeView={activeView} />
    </>
  );
};

const StyledDashboardWrapper = styled.div`
  background: var(--mantine-color-gray-0);
  border: ${(props) => `4px solid var(--mantine-color-${props.color}-7)`};
  border-radius: 0 0 var(--mantine-radius-md) var(--mantine-radius-md);
  padding: ${(props) =>
    props.isVehicleDataEffort ? "20px 10px 20px 10px" : "100px 10px 20px 10px"};
  margin-top: ${(props) => (props.isVehicleDataEffort ? "0" : "-125px")};

  @media (max-width: 768px) {
    padding: ${(props) =>
    props.isVehicleDataEffort
      ? "20px 10px 20px 10px"
      : "80px 10px 20px 10px"};
  }
`;

export const LocationInfo = ({
  info,
  color,
  title,
  showGeo,
  onClick = () => { },
}) => {
  return (
    <StyledLocationInfo onClick={onClick}>
      <Flex gap="md" align="center">
        {info.logo_url ? (
          <StyledImage
            src={info.logo_url}
            alt={info.name}
            height={60}
            width={60}
          />
        ) : (
          <StyledPlaceholder height={60} width={60} />
        )}
        <div style={{ flexGrow: 1 }}>
          <Flex gap="sm" align="center">
            <Text
              c={`var(--mantine-color-${color}-7)`}
              size="xs"
              style={{ textTransform: "uppercase" }}
              fw={600}
            >
              {title}
            </Text>
            {showGeo && <IconLocationFilled size={10} />}
          </Flex>
          <Text fw={600} style={{ textTransform: "uppercase" }}>
            {info.name}
          </Text>
          {info.city && (
            <Text size="sm">
              {info.city}, {info.state_province}
            </Text>
          )}
        </div>
      </Flex>
    </StyledLocationInfo>
  );
};

const StyledLocationInfo = styled.div`
  width: 100%;
  padding: 0 10px;

  img {
    width: 60px;
    height: 60px;
    margin: 0 auto;
    object-fit: contain;
    border-radius: 5px;
  }

  svg path {
    fill: var(--mantine-color-gray-4);
  }
`;

const StyledUnverifiedReadings = styled.div`
  width: 100%;
  background: var(--mantine-color-red-8);
  padding: 10px;
  border-radius: var(--mantine-radius-md) var(--mantine-radius-md) 0 0;
  margin-bottom: 15px;
`;

const PreferredLocationModal = ({ setView }) => {
  return (
    <Modal opened={true} onClose={() => { }} withCloseButton={false}>
      <Stack>
        <Text align="center" size="lg" fw={600}>
          Choose Your Home Dealership
        </Text>
        <Text align="center" size="lg">
          This is your preferred, regular dealership.
        </Text>
        <Text align="center" size="lg">
          Your miles will be credited to this dealership—even if you sign up or
          ride elsewhere.
        </Text>
        <Button fullWidth onClick={setView}>
          Set Home Dealership
        </Button>
      </Stack>
    </Modal>
  );
};
